/* The design model, its defaults, and URL serialisation.

   Text sizes are stored in millimetres because that is what the renderer and
   the PDF both speak; the inspector shows points and converts on the way in
   and out. Tracking is a fraction of the font size, matching CSS em units. */

window.JCARD = window.JCARD || {};

JCARD.state = (function () {
  const PT_PER_MM = 2.834645669;
  const mmToPt = (mm) => mm * PT_PER_MM;
  const ptToMm = (pt) => pt / PT_PER_MM;

  const DEFAULT_TRACK = 'Song Name · Artist Name';

  function defaultTracks() {
    return Array.from({ length: 12 }, () => DEFAULT_TRACK);
  }

  // Order matters: this drives the field list in the inspector rail.
  const FIELD_ORDER = [
    'albumTitle', 'artistName', 'additionalText', 'spineText', 'flapLabel', 'attributionFront',
    'insideSpine', 'credits', 'attributionBack', 'tracksA', 'tracksB',
  ];

  /* maxLines and maxChars are the input-side guard: they stop absurd text
     getting in at all. They are not the only guard — the renderer scales any
     line that would still overrun its panel, so a long string at a large size
     shrinks rather than spilling off the card. Both are needed, because the
     user can change the size and family after typing. */
  const FIELD_META = {
    albumTitle: { label: 'Album title', page: 'outside', maxLines: 1, maxChars: 32 },
    artistName: { label: 'Artist name', page: 'outside', maxLines: 1, maxChars: 34 },
    additionalText: { label: 'Additional text', page: 'outside', maxLines: 4, maxChars: 42 },
    spineText: { label: 'Spine', page: 'outside', maxLines: 2, maxChars: 56 },
    flapLabel: { label: 'Flap label', page: 'outside', maxLines: 1, maxChars: 48 },
    attributionFront: { label: 'Attribution (outside)', page: 'outside', maxLines: 1, maxChars: 48 },
    insideSpine: { label: 'Spine', page: 'inside', maxLines: 2, maxChars: 48 },
    credits: { label: 'Credits', page: 'inside', maxLines: 1, maxChars: 48 },
    attributionBack: { label: 'Attribution (inside)', page: 'inside', maxLines: 1, maxChars: 48 },
    tracksA: { label: 'Side A tracks', page: 'inside', isTrackList: true, maxChars: 40 },
    tracksB: { label: 'Side B tracks', page: 'inside', isTrackList: true, maxChars: 40 },
  };

  /* Trims text to the field's line and character limits. Single-line fields
     have newlines collapsed to spaces rather than dropped, so pasting a
     paragraph degrades to one readable line instead of losing words. */
  function clamp(id, text) {
    const meta = FIELD_META[id];
    const maxLines = meta.maxLines || 1;
    const maxChars = meta.maxChars || 40;
    const source = maxLines === 1 ? [String(text).replace(/[\r\n]+/g, ' ')] : String(text).split(/\r?\n/);
    return source.slice(0, maxLines).map((line) => line.slice(0, maxChars)).join('\n');
  }

  /* The track lists hold one line per column rather than a single string, so
     they get their own clamp. Both this and clamp() are applied wherever text
     enters the model — typing, and reading a shared link — so no path can put
     over-length text in. */
  function clampTracks(id, lines) {
    const count = JCARD.geom.inside.columns.count;
    const maxChars = FIELD_META[id].maxChars;
    const out = (Array.isArray(lines) ? lines : [])
      .slice(0, count)
      .map((line) => String(line == null ? '' : line).replace(/[\r\n]+/g, ' ').slice(0, maxChars));
    while (out.length < count) out.push('');
    return out;
  }

  function clampAll(model) {
    FIELD_ORDER.forEach((id) => {
      const field = model.fields[id];
      if (!field) return;
      if (FIELD_META[id].isTrackList) field.lines = clampTracks(id, field.lines);
      else field.text = clamp(id, field.text);
    });
    return model;
  }

  function defaults() {
    const g = JCARD.geom;
    return {
      fields: {
        albumTitle: {
          text: 'ALBUM TITLE', font: 'Archivo', weight: 700,
          size: g.outside.albumTitle.size, color: '#999A9D', tracking: 0.01, align: 'start',
        },
        artistName: {
          text: 'Artist Name', font: 'Libre Franklin', weight: 400,
          size: g.outside.artistName.size, color: '#999A9D', tracking: 0, align: 'start',
        },
        additionalText: {
          text: 'ADDITIONAL TEXT PLACEHOLDER\nUP TO FOUR LINES',
          font: 'Archivo', weight: 400,
          size: g.outside.formatLine.size, color: '#B2B2B4', tracking: 0.09, align: 'start',
        },
        spineText: {
          text: 'SPINE TEXT · SIDE A / B', font: 'Archivo', weight: 400,
          size: 2.3, color: '#9A9A9D', tracking: 0.14, align: 'middle',
        },
        flapLabel: {
          text: 'LABEL / CATALOG NO.', font: 'Archivo', weight: 400,
          size: 2.1, color: '#9A9A9D', tracking: 0.1, align: 'middle',
        },
        attributionFront: {
          text: 'TEMPLATE BY ULTRAFERRIC.COM', font: 'Archivo', weight: 400,
          size: 1.8, color: '#9A9A9D', tracking: 0.1, align: 'end',
        },
        insideSpine: {
          text: 'SIDE A / SIDE B', font: 'Archivo', weight: 400,
          size: 2.3, color: '#9A9A9D', tracking: 0.12, align: 'middle',
        },
        credits: {
          text: 'CREDITS · © YEAR', font: 'Archivo', weight: 400,
          size: 2.1, color: '#9A9A9D', tracking: 0.1, align: 'middle',
        },
        attributionBack: {
          text: 'TEMPLATE BY ULTRAFERRIC.COM', font: 'Archivo', weight: 400,
          size: 1.8, color: '#9A9A9D', tracking: 0.1, align: 'end',
        },
        tracksA: {
          mode: 'text', lines: defaultTracks(), numbered: true,
          font: 'Archivo', weight: 400, size: g.inside.trackText.size,
          color: '#8E8E91', tracking: 0.02,
        },
        tracksB: {
          mode: 'blank', lines: defaultTracks(), numbered: true,
          font: 'Archivo', weight: 400, size: g.inside.trackText.size,
          color: '#8E8E91', tracking: 0.02,
        },
      },

      cover: {
        src: null,
        placement: 'inset',   // inset | bleed | full
        fit: 'fill',          // fill | fit | stretch
        overlayText: true,
      },

      colors: {
        background: '#FFFFFF',
        rules: '#C6BBA4',
        bars: '#1F1F21',
        badgeText: '#FFFFFF',
      },

      toggles: {
        dividerRule: true,
        spineBars: true,
        blockDivider: true,
        sideBars: true,        // the heavy bar down the edge of each block
        sideBadges: true,      // the round A / B marks
        cropMarks: true,
        foldGuides: true,       // export only; the preview always shows them
      },

      duplex: 'long',           // long | short
    };
  }

  let current = defaults();
  const listeners = [];

  function get() { return current; }

  function subscribe(fn) { listeners.push(fn); return fn; }

  function notify(opts) { listeners.forEach((fn) => fn(current, opts || {})); }

  /* `quiet` means the change came from a control the user is still interacting
     with — typing in a field, dragging a colour picker. The preview repaints
     but the rail is left alone, because rebuilding it would destroy the very
     input holding the caret. */
  function update(mutator, opts) {
    mutator(current);
    notify(opts);
  }

  function reset() {
    const src = current.cover.src;
    current = defaults();
    current.cover.src = src;
    notify();
  }

  /* Only the difference from the template is serialised. An untouched design
     encodes to almost nothing, and a typical edited one stays well under the
     2 KB that the least generous link handlers will carry. */
  function diff(base, value) {
    if (Array.isArray(value)) {
      return JSON.stringify(base) === JSON.stringify(value) ? undefined : value;
    }
    if (value === null || typeof value !== 'object') {
      return base === value ? undefined : value;
    }
    const out = {};
    let any = false;
    Object.keys(value).forEach((k) => {
      const d = diff(base ? base[k] : undefined, value[k]);
      if (d !== undefined) { out[k] = d; any = true; }
    });
    return any ? out : undefined;
  }

  /* URL round-trip. The cover image is deliberately excluded — a data URL
     would blow past every practical URL limit — so a shared link carries the
     layout and the recipient re-attaches artwork. */
  function toHash() {
    const copy = JSON.parse(JSON.stringify(current));
    copy.cover.src = null;
    const json = JSON.stringify(diff(defaults(), copy) || {});
    const bytes = new TextEncoder().encode(json);
    let bin = '';
    bytes.forEach((b) => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromHash(hash) {
    if (!hash) return false;
    try {
      const b64 = hash.replace(/-/g, '+').replace(/_/g, '/');
      const bin = atob(b64);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      const parsed = JSON.parse(new TextDecoder().decode(bytes));
      current = clampAll(deepMerge(defaults(), parsed));
      notify();
      return true;
    } catch (err) {
      console.warn('Could not read design from URL:', err.message);
      return false;
    }
  }

  function deepMerge(base, patch) {
    if (Array.isArray(patch)) return patch.slice();
    if (patch === null || typeof patch !== 'object') return patch === undefined ? base : patch;
    const out = Array.isArray(base) ? base.slice() : Object.assign({}, base);
    Object.keys(patch).forEach((k) => {
      out[k] = k in base ? deepMerge(base[k], patch[k]) : patch[k];
    });
    return out;
  }

  return {
    FIELD_ORDER, FIELD_META, DEFAULT_TRACK,
    defaults, defaultTracks, get, update, reset, subscribe, clamp, clampTracks,
    toHash, fromHash, mmToPt, ptToMm,
  };
})();
